import {StoreFeedItem} from "../../redux/storeFeed/storeFeedSlice";
import {ImageModel} from "../../models/image";
import {Image} from "../images/Image"
import {ImageUtils} from "../../utils/imageUtils";
import {Heading1, Heading2, Heading4, Paragraph} from "../text";
import React from "react";
import {ArrowNextExtendedIcon, ArrowNextIcon} from "../icons";
import Skeleton from "react-loading-skeleton";
import {useTranslation} from "next-i18next";
import {useRouter} from "next/router";
import Link from "next/link";

export interface StoreFeedImageProps {
  image?: ImageModel
  isLoading: boolean
  className?: string
}

const StoreFeedVendorIconImage: React.FC<StoreFeedImageProps> = ({ image, className, isLoading }) => {
  const imageUrl = ImageUtils.createUrlFromImage(image, { heightPx: 200, widthPx: 200 })
  if (isLoading) {
    return <div className={`h-10 w-10 lg:h-16 lg:w-16 ${className}`}>
      <Skeleton circle width="100%" height="100%" />
    </div>
  }
  return <div className={className}>
    {imageUrl ?
        <Image
            src={imageUrl}
            alt="Company Logo"
            objectFit="cover"
            sizeClass="h-10 w-10 lg:h-16 lg:w-16 ring-fg-gray-surface ring-1 rounded-full"
            className="rounded-full bg-fg-gray-surface"
        /> : <div className="h-10 w-10 lg:h-16 lg:w-16 ring-fg-gray-surface ring-1 rounded-full bg-fg-gray-surface" />
    }
  </div>
}

const StoreFeedVendorProductImage: React.FC<StoreFeedImageProps> = ({ image, className, isLoading }) => {
  const imageUrl = ImageUtils.createUrlFromImage(image, { heightPx: 96, widthPx: 96 })

  if (isLoading) {
    return <div className={`rounded-xl aspect-w-1 aspect-h-1 max-h-28 max-w-28 ${className}`}>
      <Skeleton width="100%" height="100%" className="!rounded-xl" />
    </div>
  }

  return <div className={className}>
    {imageUrl &&
    <Image
        src={imageUrl}
        alt={"product"}
        layout="fill"
        objectFit="cover"
        sizeClass="aspect-w-1 aspect-h-1 max-h-28 max-w-28"
        className="rounded-xl bg-fg-gray-surface"
    />}
  </div>
}


export interface StoreFeedVendorInfoProps {
  store: StoreFeedItem
  isLoading: boolean
  className?: string
}

const StoreFeedVendorInfo: React.FC<StoreFeedVendorInfoProps> = ({ store, className, isLoading }) => {
  const attrs = store.storeAttributes
  return <div className={className}>
    <Heading2 className="mb-2">{isLoading ? <Skeleton width="30%"/> : store.title}</Heading2>
    <Paragraph className="text-fg-gray-dark mb-2">{isLoading ? <Skeleton width="60%"/> : store.subtitle}</Paragraph>
    <div className={`overflow-x-auto flex flex-row space-x-2 ${attrs && attrs.length > 0 ? '' : 'hidden'}`}>
      {attrs?.map((attr) => {
        return isLoading ? <Skeleton className="!w-20 !h-10 !rounded-full" key={attr.id} /> :  <div className="rounded-full bg-fg-gray-surface px-4 py-2 flex items-center text-center" key={attr.id}>
          <Heading4>{attr.text}</Heading4>
        </div>
      })}
    </div>
  </div>
}

export interface StoreFeedVendorCardViewProps {
  store: StoreFeedItem
  isLoading: boolean
}

export const StoreFeedVendorCardView: React.FC<StoreFeedVendorCardViewProps> = (props) => {
  const { t } = useTranslation()

  const totalItemCount = props.store.totalItemCount ?? 1

  return (
    <Link href={props.isLoading ? '#' : `/store/${props.store.id}`} passHref>
      <a
        className="transition-colors bg-fg-gray-surface-light lg:bg-transparent hover:bg-fg-gray-surface-light rounded-xl flex flex-row space-x-3 lg:space-x-6 group px-4 py-4 lg:px-8 lg:py-8"
      >
        <StoreFeedVendorIconImage image={props.store.iconImage} isLoading={props.isLoading} className="flex-none" />
        <div className="flex-grow">
          <div className="flex space-x-4 items-start mb-4">
            <StoreFeedVendorInfo store={props.store} isLoading={props.isLoading} className="flex-grow" />
            {props.isLoading ? <Skeleton width="140px" height="25px" className="!hidden !lg:flex" /> : <div>
              <button
                className="transition-all hidden lg:flex group-hover:bg-fg-black group-hover:text-white bg-transparent text-fg-black rounded-full px-4 py-2 flex-none items-center space-x-2">
                <Heading4>{t('store:store_button_shop_this_store')}</Heading4>
                <ArrowNextExtendedIcon className="h-4 w-4"/>
              </button>
              <button className="flex lg:hidden py-2">
                <ArrowNextIcon className="h-3 w-3"/>
              </button>
            </div>}
          </div>
          <div className="grid grid-cols-3 md:grid-cols-5 lg:grid-cols-8 xl:grid-cols-10 gap-3">
            {props.store.products.map((p, i) => {
              return <StoreFeedVendorProductImage
                isLoading={props.isLoading}
                className={`${i + 1 >= 3 ? 'hidden' : 'block'} md:${i + 1 >= 5 ? 'hidden' : 'block'} lg:${i + 1 >= 8 ? 'hidden' : 'block'} xl:${i + 1 >= 10 ? 'hidden' : 'block'}`}
                image={p.image}
                key={p.id}
              />
            })}
            <div className={`aspect-w-1 aspect-h-1 ${(totalItemCount - 2) > 0 ? 'md:hidden' : 'hidden'} max-h-28 max-w-28 rounded-xl ${props.isLoading ? '' : 'bg-fg-gray-surface'}`} >
              {props.isLoading ? <Skeleton className="!w-full !h-full !rounded-xl" /> : <div className="flex items-center justify-center text-center">
                <Heading1 className="align-middle">+{totalItemCount - 2}</Heading1>
              </div>}
            </div>
            <div className={`aspect-w-1 aspect-h-1 ${(totalItemCount - 4) > 0 ? 'hidden md:block lg:hidden' : 'hidden'} max-h-28 max-w-28 rounded-xl ${props.isLoading ? '' : 'bg-fg-gray-surface'}`} >
              {props.isLoading ? <Skeleton className="!w-full !h-full !rounded-xl" /> : <div className="flex items-center justify-center text-center">
                <Heading1 className="align-middle">+{totalItemCount - 4}</Heading1>
              </div>}
            </div>
            <div className={`aspect-w-1 aspect-h-1 ${(totalItemCount - 7) > 0 ? 'hidden lg:block xl:hidden' : 'hidden'} max-h-28 max-w-28 rounded-xl ${props.isLoading ? '' : 'bg-fg-gray-surface'}`} >
              {props.isLoading ? <Skeleton className="!w-full !h-full !rounded-xl" /> : <div className="flex items-center justify-center text-center">
                <Heading1 className="align-middle">+{totalItemCount - 7}</Heading1>
              </div>}
            </div>
            <div className={`aspect-w-1 aspect-h-1 ${(totalItemCount - 9) > 0 ? 'hidden xl:block' : 'hidden'} max-h-28 max-w-28 rounded-xl ${props.isLoading ? '' : 'bg-fg-gray-surface'}`} >
              {props.isLoading ? <Skeleton className="!w-full !h-full !rounded-xl" /> : <div className="flex items-center justify-center text-center">
                <Heading1 className="align-middle">+{totalItemCount - 9}</Heading1>
              </div>}
            </div>
          </div>
        </div>
      </a>
    </Link>
  )
}